import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { patchTrip, addRound, deleteRound, getTrip } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, UsersThree, Golf, CheckCircle, Plus, Trash, CaretRight, Copy, ShieldCheck } from '@phosphor-icons/react';
import AddPlayerForm from '../components/AddPlayerForm';

const STEPS = [
  { key: 'players', label: 'Players', Icon: UsersThree },
  { key: 'rounds', label: 'Rounds', Icon: Golf },
  { key: 'summary', label: 'Summary', Icon: CheckCircle },
];

export const WIZARD_RETURN_KEY = 'foretour:wizard-return';

export default function TripSetupWizard({ trip: initialTrip, code, returnToStep = 0, onComplete }) {
  const navigate = useNavigate();
  const [trip, setTrip] = useState(initialTrip);
  const [step, setStep] = useState(returnToStep);
  const [saving, setSaving] = useState(false);
  const [pinCopied, setPinCopied] = useState(false);
  const [upwCopied, setUpwCopied] = useState(false);

  // Check if the PIN was auto-generated (flag set by CreateEvent).
  // NOTE: Do NOT remove the key here — StrictMode double-mounts, so the
  // second initializer would see it gone. Clean up in handleFinish instead.
  const [pinAutoGenerated] = useState(() => {
    return sessionStorage.getItem('foretour:pin-auto') === code;
  });

  const players = trip?.players || [];
  const rounds = trip?.rounds || [];

  // ── Player save callback for shared AddPlayerForm ──
  const handleSavePlayers = async (updatedPlayers) => {
    setSaving(true);
    try {
      const res = await patchTrip(code, { players: updatedPlayers });
      setTrip(res.data);
    } catch {} finally { setSaving(false); }
  };

  // ── Round actions (same API as TripDetail.handleAddRound) ──
  const handleAddRound = async () => {
    setSaving(true);
    const label = `Round ${rounds.length + 1}`;
    const defaultCourse = { id: 'custom', name: 'Select Course', par: 72, course_rating: 72, slope_rating: 113 };
    try {
      const res = await addRound(code, { label, course: defaultCourse });
      setTrip(res.data);
    } catch {} finally { setSaving(false); }
  };

  const handleDeleteRound = async (roundId) => {
    setSaving(true);
    try {
      // Wizard user is always organiser (just created trip), header carries organiser role.
      // Send empty pin — backend accepts via header auth for non-legacy.
      // For legacy, trip.pin is available in the same session.
      const res = await deleteRound(code, roundId, { pin: null, confirm_round_id: roundId });
      setTrip(res.data);
    } catch {} finally { setSaving(false); }
  };

  const [wizardRoundDeleteTarget, setWizardRoundDeleteTarget] = useState(null);

  // Refetch fresh trip data before showing summary.
  // GET /trips/{code} includes pin + user_password for organisers and
  // guarantees counts reflect all committed writes (guards against any
  // silently-failed PATCH during player / round addition).
  const goToSummary = async () => {
    try {
      const res = await getTrip(code);
      if (res.status === 200) setTrip(res.data);
    } catch { /* fall through — summary renders from last known state */ }
    setStep(2);
  };

  // Navigate to course picker for a round, with return flag
  const handleConfigureRound = (roundId) => {
    sessionStorage.setItem(WIZARD_RETURN_KEY, code);
    navigate(`/trip/${code}/round/${roundId}/course`);
  };

  // ── Finish / Skip ──
  const handleFinish = async () => {
    sessionStorage.removeItem('foretour:pin-auto');
    setSaving(true);
    try {
      const res = await patchTrip(code, { setup_completed: true });
      onComplete(res.data);
    } catch {
      onComplete(trip);
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="trip-setup-wizard">
      {/* Header */}
      <div className="bg-[#1D242B] px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/home')} className="text-white p-1" data-testid="wizard-back-btn">
            <ArrowLeft className="w-5 h-5" weight="bold" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg text-white font-bold truncate">{trip?.name || 'New Event'}</h1>
            <p className="text-xs text-[#B8C7C0]">Setup your event</p>
          </div>
          <Badge className="bg-[#E8E5DB] text-[#1B4332] text-xs font-mono px-2">{code}</Badge>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1" data-testid="wizard-stepper">
          {STEPS.map((s, i) => {
            const active = i === step;
            const done = i < step;
            return (
              <button key={s.key} onClick={() => i === 2 ? goToSummary() : setStep(i)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                  active ? 'bg-[#1B4332] text-white' : done ? 'bg-[#2C3742] text-[#B8C7C0]' : 'bg-[#2C3742] text-[#5A6670]'
                }`}
                data-testid={`wizard-step-${s.key}`}>
                <s.Icon className="w-3.5 h-3.5" weight={active || done ? 'fill' : 'regular'} />
                <span>{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-6 max-w-lg mx-auto">

        {/* ── Step 1: Players ── */}
        {step === 0 && (
          <div className="space-y-4 animate-fade-in-up" data-testid="wizard-step-players-content">
            <div>
              <h2 className="text-lg font-bold text-[#1D242B]">Add your players</h2>
              <p className="text-sm text-[#455561] mt-1">Enter each player's name and WHS handicap index.</p>
            </div>

            <AddPlayerForm players={players} onSavePlayers={handleSavePlayers} saving={saving} />

            <Button onClick={() => setStep(1)} className="w-full h-14 bg-[#1D242B] hover:bg-[#2C3742] text-white rounded-xl text-base font-semibold" data-testid="wizard-next-rounds">
              Next — Add Rounds
            </Button>
          </div>
        )}

        {/* ── Step 2: Rounds ── */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-in-up" data-testid="wizard-step-rounds-content">
            <div>
              <h2 className="text-lg font-bold text-[#1D242B]">Add your rounds</h2>
              <p className="text-sm text-[#455561] mt-1">Create rounds and tap them to select a course.</p>
            </div>

            <Button onClick={handleAddRound} disabled={saving}
              className="w-full h-14 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl text-base font-semibold" data-testid="wizard-add-round-btn">
              <Plus className="w-5 h-5 mr-2" weight="bold" /> {saving ? 'Adding...' : 'Add Round'}
            </Button>

            {rounds.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-[#455561] uppercase tracking-wide">{rounds.length} round{rounds.length !== 1 ? 's' : ''} added</p>
                {rounds.map(r => {
                  const needsCourse = !r.course?.id || r.course?.id === 'custom' || r.course?.name === 'Select Course';
                  return (
                    <Card key={r.id} className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleConfigureRound(r.id)} data-testid={`wizard-round-${r.id}`}>
                      <div className="flex items-center px-4 py-3">
                        <Golf className="w-5 h-5 text-[#1B4332] mr-3 flex-shrink-0" weight="fill" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold text-[#1D242B]">{r.label}</span>
                          <p className="text-xs text-[#455561]">{r.course?.name || 'No course'} · Par {r.course?.par || '--'}</p>
                        </div>
                        {needsCourse && (
                          <Badge className="bg-[#E59000] text-white text-[10px] font-semibold mr-2 flex-shrink-0">Select Course</Badge>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); setWizardRoundDeleteTarget(r); }} className="p-1.5 text-[#B8C7C0] hover:text-[#D32F2F] mr-1 flex-shrink-0" data-testid={`wizard-delete-round-${r.id}`}>
                          <Trash className="w-4 h-4" weight="bold" />
                        </button>
                        <CaretRight className="w-4 h-4 text-[#B8C7C0] flex-shrink-0" />
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            <Button onClick={goToSummary} className="w-full h-14 bg-[#1D242B] hover:bg-[#2C3742] text-white rounded-xl text-base font-semibold" data-testid="wizard-next-summary">
              Next — Summary
            </Button>
          </div>
        )}

        {/* ── Step 3: Summary ── */}
        {step === 2 && (
          <div className="space-y-6 animate-fade-in-up" data-testid="wizard-step-summary-content">
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-[#1B4332] mx-auto mb-3" weight="fill" />
              <h2 className="text-lg font-bold text-[#1D242B]">You're all set</h2>
              <p className="text-sm text-[#455561] mt-1">Here's a summary of your event setup.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl text-center" data-testid="wizard-summary-players">
                <UsersThree className="w-6 h-6 text-[#1B4332] mx-auto mb-1" weight="fill" />
                <p className="text-2xl font-bold text-[#1D242B]">{players.length}</p>
                <p className="text-xs text-[#455561]">Player{players.length !== 1 ? 's' : ''}</p>
              </Card>
              <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl text-center" data-testid="wizard-summary-rounds">
                <Golf className="w-6 h-6 text-[#1B4332] mx-auto mb-1" weight="fill" />
                <p className="text-2xl font-bold text-[#1D242B]">{rounds.length}</p>
                <p className="text-xs text-[#455561]">Round{rounds.length !== 1 ? 's' : ''}</p>
              </Card>
            </div>

            {/* Trip capacity & access */}
            <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl" data-testid="wizard-summary-caps">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-5 h-5 text-[#1B4332]" weight="fill" />
                <span className="font-bold text-sm text-[#1D242B]">Trip capacity & access</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-[#F4F6F5] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#1D242B]" data-testid="wizard-cap-rounds">
                    {trip?.legacy_unlimited ? 'Unlimited' : (trip?.max_rounds ?? '—')}
                  </p>
                  <p className="text-[10px] text-[#455561] font-semibold uppercase tracking-wide">Max Rounds</p>
                </div>
                <div className="bg-[#F4F6F5] rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-[#1D242B]" data-testid="wizard-cap-players">
                    {trip?.legacy_unlimited ? 'Unlimited' : (trip?.max_players ?? '—')}
                  </p>
                  <p className="text-[10px] text-[#455561] font-semibold uppercase tracking-wide">Max Players</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between bg-[#F4F6F5] rounded-lg px-3 py-2.5">
                  <div>
                    <p className="text-[10px] text-[#455561] font-semibold uppercase tracking-wide">Organiser code</p>
                    <p className="text-base font-bold font-mono tracking-widest text-[#1D242B]" data-testid="wizard-cap-pin">{trip?.pin || '—'}</p>
                  </div>
                  {trip?.pin && (
                    <button onClick={() => { navigator.clipboard?.writeText(trip.pin); setPinCopied(true); setTimeout(() => setPinCopied(false), 1500); }}
                      className="p-1.5 rounded-lg bg-white border border-[#E1E6E4] text-[#455561] hover:text-[#1B4332] transition-colors" data-testid="wizard-cap-pin-copy">
                      {pinCopied ? <CheckCircle className="w-4 h-4 text-[#1B4332]" weight="fill" /> : <Copy className="w-4 h-4" weight="bold" />}
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between bg-[#F4F6F5] rounded-lg px-3 py-2.5">
                  <div>
                    <p className="text-[10px] text-[#455561] font-semibold uppercase tracking-wide">Player code</p>
                    <p className="text-base font-bold font-mono tracking-widest text-[#1D242B]" data-testid="wizard-cap-upw">{trip?.user_password || '—'}</p>
                  </div>
                  {trip?.user_password && (
                    <button onClick={() => { navigator.clipboard?.writeText(trip.user_password); setUpwCopied(true); setTimeout(() => setUpwCopied(false), 1500); }}
                      className="p-1.5 rounded-lg bg-white border border-[#E1E6E4] text-[#455561] hover:text-[#1B4332] transition-colors" data-testid="wizard-cap-upw-copy">
                      {upwCopied ? <CheckCircle className="w-4 h-4 text-[#1B4332]" weight="fill" /> : <Copy className="w-4 h-4" weight="bold" />}
                    </button>
                  )}
                </div>
              </div>
            </Card>

            {/* Auto-generated PIN display */}
            {pinAutoGenerated && trip?.pin && (
              <Card className="p-4 bg-[#FDF8E8] border-[#C9A227] border-2 rounded-xl" data-testid="wizard-pin-card">
                <p className="text-sm font-bold text-[#1D242B] mb-1">Your trip PIN is</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold text-[#C9A227] font-mono tracking-widest" data-testid="wizard-pin-value">{trip.pin}</span>
                  <button onClick={() => { navigator.clipboard?.writeText(trip.pin); setPinCopied(true); setTimeout(() => setPinCopied(false), 1500); }}
                    className="p-2 rounded-lg bg-white border border-[#E1E6E4] text-[#455561] hover:text-[#1B4332] transition-colors" data-testid="wizard-pin-copy">
                    {pinCopied ? <CheckCircle className="w-5 h-5 text-[#1B4332]" weight="fill" /> : <Copy className="w-5 h-5" weight="bold" />}
                  </button>
                </div>
                <p className="text-xs text-[#455561] mt-2">Save this now — it can't be recovered from the app later. You'll need it to manage player access.</p>
              </Card>
            )}

            {(players.length === 0 && rounds.length === 0) && (
              <p className="text-sm text-[#E59000] text-center">You haven't added any players or rounds yet. You can always add them from the dashboard.</p>
            )}

            <Button onClick={handleFinish} disabled={saving}
              className="w-full h-14 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl text-base font-bold" data-testid="wizard-finish-btn">
              {saving ? 'Finishing...' : 'Go to trip dashboard'}
            </Button>
            <Button onClick={() => setStep(1)} variant="outline"
              className="w-full h-12 rounded-xl border-[#E1E6E4] text-[#455561]" data-testid="wizard-back-btn-summary">
              Back
            </Button>
          </div>
        )}

        {/* Skip link */}
        <button onClick={handleFinish} className="w-full text-center text-sm text-[#B8C7C0] hover:text-[#455561] py-4 mt-2 transition-colors" data-testid="wizard-skip">
          Skip setup for now
        </button>
      </div>

      {/* Wizard round delete confirmation */}
      <Dialog open={!!wizardRoundDeleteTarget} onOpenChange={(open) => { if (!open) setWizardRoundDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-sm rounded-2xl">
          <DialogHeader><DialogTitle>Delete Round?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#455561] py-1">Delete <strong>{wizardRoundDeleteTarget?.label}</strong>?</p>
          <DialogFooter className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => setWizardRoundDeleteTarget(null)} className="flex-1 rounded-xl">Cancel</Button>
            <Button onClick={() => { handleDeleteRound(wizardRoundDeleteTarget?.id); setWizardRoundDeleteTarget(null); }}
              className="flex-1 bg-[#D32F2F] hover:bg-red-700 text-white rounded-xl" data-testid="wizard-confirm-delete-round">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
